<template>
  <div class="order-notification">
    <div v-if="!notifications || notifications.length === 0">
      {{ t("Notifications not found") }}
    </div>

    <div v-else class="order-notification-wrapper d-flex flex-column">
      <v-radio-group
        v-model="selectedNotificationState"
        hide-details
        class="mt-5"
      >
        <v-radio
          :label="t('Don\'t receive any notifications for this order')"
          :value="false"
        />
        <v-radio
          :label="t('Receive notifications for the following status changes')"
          :value="true"
        />
      </v-radio-group>

      <div
        class="notifications"
      >
        <v-checkbox
          v-for="n in notifications"
          :key="String(n.notificationId)"
          :model-value="isNotificationChecked(n)"
          :label="t(n.name)"
          :disabled="isNotificationReadOnly(n)"
          hide-details
          @update:model-value="checked => onToggle(n, checked)"
        />

      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Notification } from '@/entities/notification';
import { useNotificationSelectionRules } from '@/composables/useNotificationSelectionRules';

const { t } = useI18n();

const {
  isForcedForExternalUser,
  isNotificationChecked: isNotificationCheckedByRules,
  isNotificationReadOnly: isNotificationReadOnlyByRules,
} = useNotificationSelectionRules();

const props = defineProps<{
  orderNotifications: Notification[];
  notifications: Notification[];
}>();

const emit = defineEmits<{
  (e: 'save', payload: { remove?: Notification; add?: Notification }, options?: { silent?: boolean }): void
  (e: 'removeAllNotifications', payload: Notification[]): void
}>();

const selectedNotifications = ref<string[]>(
  (props.orderNotifications ?? []).map(n => String(n.notificationId))
);

const selectedNotificationState = ref<boolean>(selectedNotifications.value.length > 0);

const mandatoryNotification = computed(
  () => (props.notifications ?? []).find(n => n.isMandatory) ?? null
);

function isNotificationChecked(notification: Notification): boolean {
  return isNotificationCheckedByRules(
    notification,
    selectedNotificationState.value,
    selectedNotifications.value,
    true
  );
}

function isNotificationReadOnly(notification: Notification): boolean {
  return isNotificationReadOnlyByRules(notification, selectedNotificationState.value);
}

function onToggle(item: Notification, checked: boolean) {
  if (isForcedForExternalUser(item)) {
    return;
  }

  const id = String(item.notificationId);

  if (checked) {
    if (!selectedNotifications.value.includes(id)) {
      selectedNotifications.value = [...selectedNotifications.value, id];
      emit('save', { add: { name: item.name, notificationId: item.notificationId } });
    }
  } else {
    if (selectedNotifications.value.includes(id)) {
      selectedNotifications.value = selectedNotifications.value.filter(x => x !== id);
      emit('save', { remove: { name: item.name, notificationId: item.notificationId } });
    }
  }

  selectedNotificationState.value = selectedNotifications.value.length > 0;
}

function ensureMandatoryForExternal() {
  const mandatory = mandatoryNotification.value;
  if (!mandatory) return;

  const shouldBeChecked =
    isForcedForExternalUser(mandatory) || selectedNotificationState.value === true;

  if (!shouldBeChecked) return;

  const alreadyThere = (props.orderNotifications ?? []).some(
    n => String(n.notificationId) === String(mandatory.notificationId)
  );

  if (!alreadyThere) {
    emit(
      'save',
      { add: { name: mandatory.name, notificationId: mandatory.notificationId } },
      { silent: true }
    );
  }
}

function syncWithProps() {
  selectedNotifications.value = (props.orderNotifications ?? []).map(n => String(n.notificationId));
  selectedNotificationState.value = selectedNotifications.value.length > 0;
}

function syncAll() {
  syncWithProps();
  ensureMandatoryForExternal();
}

onMounted(syncAll);

watch(() => props.orderNotifications, syncAll);

watch(selectedNotificationState, (val, old) => {
  ensureMandatoryForExternal();

  if (old === true && val === false) {
    const removed = (props.notifications ?? []).filter(
      n =>
        !isForcedForExternalUser(n) &&
        selectedNotifications.value.includes(String(n.notificationId))
    );

    selectedNotifications.value = selectedNotifications.value.filter((id) => {
      const desc = props.notifications.find(
        (n) => String(n.notificationId) === String(id)
      );
      return desc ? isForcedForExternalUser(desc) : false;
    });

    if (removed.length) emit("removeAllNotifications", removed);
  }
});
</script>

<style lang="scss">
.order-notification {
  .v-radio:first-child {
    order: 3;
  }

  .v-label {
    opacity: var(--v-radio-label-opacity) !important;
  }

  .v-selection-control--dirty {
    .v-icon {
      color: rgb(var(--v-theme-primary)) !important;
    }
  }

  .notifications {
    min-height: 0;
    flex: 1 1 auto;

    order: 2;
    padding-left: 1.875rem;
    margin-top: 8px;
    margin-bottom: 8px;
  }

  .v-radio:last-child {
    order: 1;
    margin-top: 12px;
  }

  .v-radio-group.v-input,
  .v-radio-group.v-input > .v-input__control,
  .v-radio-group.v-input > .v-input__control > .v-selection-control-group {
    display: contents;
  }

  .v-input.v-radio-group {
    height: auto;

    .v-input__control {
      height: auto !important;
    }

    .v-selection-control-group {
      flex-direction: column;
      border: none;
      cursor: default;
      display: flex;
      width: 100%;
    }
  }
}
</style>
